import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import React, { useState } from "react"
import FormSectionWrapper from "../FormSectionWrapper"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Form, Input } from "antd"
import { Button } from "components"
import { AppPeoplePicker } from "components/inputs/select/AppPeoplePicker"
import { useNavigate } from "react-router-dom"
import { PersonsEntityDocs } from "pages/dashboard/documents/assets/typing"
import { useAppNotificationStore } from "stores"
import {
  ANNOUNCEMENTS_ENDPOINTS,
  announcementsService,
} from "services/announcementsService"
import {
  ADMIN_PEOPLE_ENDPOINTS,
  adminPeopleService,
} from "services/adminPeopleService"
import { onQueryError } from "utils/utils"

type Props = {}

const className = "px-4 md:px-6 xl:px-[60px] pb-4"

const CreateAnnouncementGroup = (props: Props) => {
  const [activeForm, setActiveForm] = useState(0)
  const [form] = Form.useForm()
  const [peopleForm] = Form.useForm()
  const queryClient = useQueryClient()
  const [employees, setEmployees] = useState<string[]>([])

  const navigate = useNavigate()
  const { toast } = useAppNotificationStore()

  const { isLoading: isGetPeopleLoading, data: peopleDataRes } = useQuery<
    IBaseRes<PersonsEntityDocs>
  >({
    queryKey: [
      ADMIN_PEOPLE_ENDPOINTS.GET_FOR_SELECTION_SIMPLE,
      { active: true },
    ],
    queryFn: async () =>
      adminPeopleService.getForSelectionSimple({ active: true }),
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
  })
  const peopleData = peopleDataRes?.data?.docs || []

  const goBackToGroupList = () =>
    navigate(`/dashboard/settings/announcement_groups`)

  const { mutate, isLoading } = useMutation({
    mutationFn: async (payload: IPayload) => {
      const peopleIDs =
        (payload?.person_list &&
          payload?.person_list?.map((it) =>
            peopleData?.find((x) => x.full_name === it)?.person_id.toString()
          )) ||
        []
      payload.person_list = peopleIDs as string[]
      return announcementsService.addGroup(payload)
    },
    onSuccess: (data: IBaseRes<any>) => {
      if (data.success) {
        data.message && toast.success(data.message)
        queryClient.invalidateQueries({
          queryKey: [ANNOUNCEMENTS_ENDPOINTS.SELECT_GROUPS],
        })
        goBackToGroupList()
      }
    },
    onError: onQueryError,
  })
  const { mutateAsync: checkGroupName } = useMutation({
    mutationFn: async (description: string) =>
      announcementsService.checkGroup(description, -1),
  })
  return (
    <DashboardWrapper>
      <div className="flex flex-col gap-6">
        <FormSectionWrapper
          isActive={activeForm === 0}
          layout="vertical"
          title="Group Title"
          form={form}
          onFinish={() => {
            setActiveForm(1)
          }}
          footerBtns={
            <div className="flex justify-end gap-4 ">
              <Button
                color="secondary"
                onClick={() => {
                  goBackToGroupList()
                }}
              >
                Back
              </Button>
              <Button
                type="button"
                color="primary"
                onClick={(e) => {
                  e.preventDefault()
                  form.submit()
                }}
              >
                Next
              </Button>
            </div>
          }
        >
          <div className={className}>
            <Form.Item
              name={"description"}
              validateTrigger="onBlur"
              validateDebounce={500}
              label="Title"
              validateStatus="validating"
              status="validating"
              rules={[
                {
                  validator(rule, value, callback) {
                    return new Promise(async (res, rej) => {
                      if (!value) {
                        return rej("Please add a group title")
                      }
                      const data = await checkGroupName(value)
                      if (data?.success) {
                        return res("")
                      }
                      if (data?.message) {
                        rej(data?.message)
                      }
                    })
                  },
                },
              ]}
            >
              <Input />
            </Form.Item>
          </div>
        </FormSectionWrapper>

        <FormSectionWrapper
          isActive={activeForm === 1}
          layout="vertical"
          title="Choose Employees"
          subtitle="Select employees that you want to add to this group"
          onFinish={(val: IPayload) => {
            val.description = form.getFieldValue("description")
            mutate(val)
          }}
          form={peopleForm}
          footerBtns={
            <div className="flex justify-end gap-4">
              <Button
                color="secondary"
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  setActiveForm(0)
                }}
              >
                Previous
              </Button>
              <Button
                submitting={isLoading}
                type="submit"
                color="primary"
                onClick={(e) => {
                  e.preventDefault()
                  peopleForm.submit()
                }}
              >
                Save
              </Button>
            </div>
          }
        >
          <div className={className}>
            <AppPeoplePicker
              employees={employees}
              fieldName="person_list"
              form={peopleForm}
              onUpdate={(items) => setEmployees(items)}
              useCustomData={false}
            />
          </div>
        </FormSectionWrapper>
      </div>
    </DashboardWrapper>
  )
}

export default CreateAnnouncementGroup

interface IPayload {
  description: string
  person_list: string[]
  update_type: string
}
